import React from 'react'
import numbro from 'numbro'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { isJustGiving } from '../../../lib/environment'
import { selectAPIParams, selectLocalisation } from '../../../store/site'
import { selectCustomSupporterPagePresent } from '../../../store/customSupporterPages'
import { deserializeLeaderboard } from 'supporticon/api/leaderboard'
import { deserializePage } from 'supporticon/api/pages'
import { useLeaderboard } from 'supporticon/hooks/use-leaderboard'
import { usePages } from '../../utils/use-pages'
import withStyles from 'constructicon/with-styles'
import styles from './styles'

import ContentSection from '../../ui/ContentSection'
import Error from '../../ui/Error'
import Flippy from 'constructicon/flippy'
import Grid from 'constructicon/grid'
import GridColumn from 'constructicon/grid-column'
import Loading from '../../ui/Loading'
import Link from '../../utils/LinkHandler'

const SupporterTiles = ({
  apiParams,
  background,
  campaign,
  charity,
  classNames,
  country,
  divider = 'none',
  event,
  excludePageIds,
  flippyBackground,
  flippyForeground,
  foreground,
  gridColumns = 6,
  group,
  hasCustomSupporterPages,
  items = [],
  limit,
  size,
  spacing,
  styles,
  title,
  titleForeground,
  type
}) => {
  const deserializeMethod = data => {
    const page = deserializePage(data)

    if (hasCustomSupporterPages) {
      return {
        ...page,
        url: `/${page.type === 'team' ? 'teams' : 'fundraising'}/${page.slug}`
      }
    }

    return page
  }

  const getData = () => {
    switch (type) {
      case 'featured':
        return usePages({
          allPages: true,
          ids: items.map((item) => item.pageID).join(',')
        }, {
          deserializeMethod
        })
      case 'all':
        return usePages({
          allPages: isJustGiving(),
          campaign: campaign || apiParams.campaignIds,
          charity: charity || apiParams.charityIds,
          event: event || apiParams.event,
          group,
          limit: (limit || 12) * 2
        }, {
          deserializeMethod
        })
      default:
        return useLeaderboard({
          campaign: campaign || apiParams.campaignIds,
          charity: charity || apiParams.charityIds,
          country,
          endDate: apiParams.endDate,
          event: event ? event.split(',') : apiParams.eventIds,
          excludePageIds,
          group,
          limit: (limit || 12) * 2,
          page: 1,
          startDate: apiParams.startDate,
          type: 'individual'
        }, {
          deserializeMethod: data => {
            const page = deserializeLeaderboard(data)

            if (hasCustomSupporterPages) {
              return {
                ...page,
                url: `/${page.type === 'team' ? 'teams' : 'fundraising'}/${page.slug}`
              }
            }

            return page
          }
        })
    }
  }

  const removeExcludedPages = (pages, excludePageIds) => {
    if (!excludePageIds) return pages
    return pages.filter(page => excludePageIds.indexOf(page.id) === -1 && excludePageIds.indexOf(page.slug) === -1)
  }

  const { data = [], status } = getData()

  const pages = removeExcludedPages(data, excludePageIds)
    .filter(page => type === 'featured' ? true : page.active)
    .slice(0, limit || 12)

  const calculateColumnSize = (gridColumns, factor) => {
    return 12 / Math.ceil(gridColumns * factor)
  }

  return (
    <ContentSection
      background={background}
      foreground={foreground}
      size={size}
      spacing={spacing}
      title={title}
      titleForeground={titleForeground}>
      {status === 'loading' && (
        <div className={classNames.root}>
          <Loading />
        </div>
      )}
      {status === 'error' && (
        <div className={classNames.root}>
          <Error />
        </div>
      )}
      {status === 'success' && (
        (
          <Grid
            align='center'
            justify='center'
            spacing={parseFloat(dividerSizes[divider])}>

            {pages.map(page => (
              <GridColumn
                key={page.slug}
                sm={calculateColumnSize(gridColumns, 0.5)}
                md={calculateColumnSize(gridColumns, 1)}>

                <Link to={page.url}>
                  <Flippy
                    front={(
                      <div
                        className={classNames.front}
                        style={{ backgroundImage: `url(${page.image})` }}
                      />
                    )}
                    back={(
                      <div className={classNames.detail}>
                        <div className={classNames.name}>{page.name}</div>
                        <div className={classNames.amount}>
                          {numbro(page.raised).formatCurrency('0,0')}
                        </div>
                      </div>
                    )}
                    background={flippyBackground}
                    foreground={flippyForeground}
                    styles={styles.flippy}
                  />
                </Link>
              </GridColumn>
            ))}
          </Grid>
        )
      )}
    </ContentSection>
  )
}

const dividerSizes = {
  none: 0,
  small: 0.125,
  medium: 0.5,
  thick: 1
}

const mapState = (store) => ({
  ...selectAPIParams(store),
  ...selectLocalisation(store),
  ...selectCustomSupporterPagePresent(store)
})

export default compose(
  withStyles(styles),
  connect(mapState)
)(SupporterTiles)
